import { LeadForm } from "@component/PersonalLoan/LeadForm";
import type {
	PersonalLoanLeadCalcRequest,
	PersonalLoanLeadCalcResponse,
	PersonalLoanLeadFormState,
} from "@component/PersonalLoan/LeadForm";
import type { LeadRequest } from "@component/assets/AbstractLeadForm/types";
import type { CalculatorError } from "@component/assets/CalculatorError";
import { JsonDataBuilder } from "@utils/JsonDataBuilder";
import type { EventComponentType } from "@utils/google-analytics-4.type";
import { logError } from "@utils/sentry-error";
import autobind from "autobind-decorator";
import compact from "lodash/compact";
import type { ReactNode } from "react";
import React from "react";
import * as Step from "./Steps";
import { calculate } from "./domain";
import type { PersonalLoanScoringCalculatorFormFields, PersonalLoanScoringResponse } from "./types";
import "./style.scss";

export interface PersonalLoanScoringCalculatorState
	extends PersonalLoanLeadFormState<PersonalLoanScoringCalculatorFormFields> {
	results: PersonalLoanScoringResponse;
	selectedMaturity: number;
	messages: string[];
}

/**
 * @Shortcode
 *
 * @title "Személyi hitel scoring kalkulátor"
 * @shortcode "personal_loans_scoring"
 * @slug "szemelyi-hitel-scoring"
 * @type "calculator"
 */
export class PersonalLoanScoringCalculator extends LeadForm<
	PersonalLoanScoringCalculatorFormFields,
	PersonalLoanScoringCalculatorState
> {
	// EventParams
	protected readonly productTypes: string = "PERSONAL_LOAN";
	protected readonly componentType: EventComponentType = "small-calculator";

	public static defaultProps = {
		outsideTitle: "Mennyi személyi hitelt kaphatok?",
		variant: "scoring-small-calculator",
		title: "Kérd szakértőnk segítségét!",
		bottomLogo: true,
	};

	protected getInitialState(): PersonalLoanScoringCalculatorState {
		return {
			...super.getInitialState(),
			activeStep: "step-1",
			results: undefined,
			messages: [],
		} as unknown as PersonalLoanScoringCalculatorState;
	}

	protected renderSteps(): ReactNode {
		const { values, results, messages, submitState, leadId } = this.state;
		const response = this.getCalculatorResponse();

		return (
			<>
				<Step.Step1 stepName="step-1" defaultTarget="step-2" className="intro-step" />
				<Step.Step2 stepName="step-2" defaultTarget="step-3" canStepBack />
				<Step.Step3 stepName="step-3" defaultTarget="step-4" canStepBack values={values} />
				<Step.Step4 stepName="step-4" defaultTarget="result" canStepBack submitState={submitState} />
				<Step.Result
					stepName="result"
					defaultTarget="lead-form"
					className="result-step"
					canStepBack
					results={results}
					selectMaturity={this.selectMaturity}
				/>
				<Step.Error stepName="error" defaultTarget="step-4" canStepBack messages={messages} />
				<Step.LeadForm stepName="lead-form" submitState={submitState} defaultTarget="success-lead" />
				<Step.SuccessPage
					stepName="success-lead"
					leadId={leadId}
					userEmail={values.email}
					ga4SendEventParameters={{
						providerName: response?.bankName,
						productName: response?.productName,
						productId: response?.id,
					}}
				/>
			</>
		);
	}

	@autobind
	private selectMaturity(maturity: number): void {
		this.setState({
			selectedMaturity: maturity,
		});
	}

	protected onStep(target: string, withEvent: boolean): void {
		if (target === "result") {
			const { values } = this.state;
			this.setState({
				submitState: "loading",
			});

			this.promisePool
				.add(calculate(values))
				.then((response) => {
					if (!Object.keys(response?.results?.availableLoans).length) {
						this.setState(
							{
								messages: response.results.messages,
								submitState: "error",
							},
							() => super.onStep("error", withEvent),
						);

						this.sendCalculationEvent(response.results.messages);
					} else {
						this.setState(
							{
								results: response.results,
								submitState: "done",
							},
							() => {
								super.onStep(target, withEvent);
							},
						);

						this.sendCalculationEvent();
					}
				})
				.catch((error: CalculatorError) => {
					this.setState(
						{
							messages: [error.message],
							submitState: "error",
						},
						() => super.onStep("error", withEvent),
					);

					this.sendCalculationEvent([error.message]);

					logError(error);
				})
				.finally(() => {
					this.promisePool.addTimeout().then(() => {
						this.setState({
							submitState: undefined,
						});
					});
				});
		} else {
			super.onStep(target, withEvent);
		}
	}

	protected getLeadData(): LeadRequest<PersonalLoanLeadCalcRequest, PersonalLoanLeadCalcResponse> {
		const { values, selectedMaturity } = this.state;
		const data = super.getLeadData();
		data.request.existingLoans = values.existingLoans;
		data.birthYear = values.birthYear;
		data.currentBank = values.receiptBank;
		data.request.maturityYears = selectedMaturity || undefined;
		const otherData = new JsonDataBuilder(data.request.otherDataJson);
		otherData.addTextItem("SCORING ÜGYFÉL", " ");
		otherData.addTextItem("Utolsó három havi jövedelem", " ");
		otherData.addItem("- 1 hónappal ezelőtti jövedelem", "lastIncome1", values.lastIncome1, "ft");
		otherData.addItem("- 2 hónappal ezelőtti jövedelem", "lastIncome2", values.lastIncome2, "ft");
		otherData.addItem("- 3 hónappal ezelőtti jövedelem", "lastIncome3", values.lastIncome3, "ft");

		if (values.existingLoans.length > 0) {
			otherData.addTextItem("Meglévő hitelek", " ");
			values.existingLoans.forEach((loan) => {
				otherData.addItem(loan.loanTypeName, `${loan.loanType}.debt`, loan.debt, "ft");
			});
		}
		data.request = { ...data.request, otherDataJson: otherData.getJson() };

		return data;
	}

	protected getClassName(): string {
		return compact([super.getClassName(), "personal-loan-scoring"]).join(" ");
	}

	protected getComponentName(): string {
		return `${super.getComponentName()}.PersonalLoanScoringCalculator`;
	}
}

export default PersonalLoanScoringCalculator;
